import { useEffect } from "react";
import useEvent from "react-use-event-hook";

import { GlobalAppEvents } from "@ryde/shared";

// Deep import, not the `../contexts` barrel: several context modules there import the `../hooks` barrel
// this file is exported from.
import { useCompanyScopeContext } from "../contexts/company-scope.context";

/**
 * Subscribe to a global app event for exactly as long as the calling component is mounted.
 *
 * Prefer this over calling `eventEmitter.on` directly. mitt matches handlers by reference, so an
 * inline arrow can never be removed again, and a component that subscribes without unsubscribing goes
 * on receiving events after it unmounts. That is worst where a listener is registered per grid cell,
 * or where the handler closes over a table instance and pins its whole row array.
 *
 * Pass `isEnabled: false` while the component has nothing to do with the event - a listener registered
 * per grid cell means one emit fans out across every cell on the page, even the ones with no open
 * popover to close.
 */
export const useEmitterListener = <TEvent extends keyof GlobalAppEvents>({
  event,
  handler,
  isEnabled = true,
}: {
  event: TEvent;
  handler: (payload: GlobalAppEvents[TEvent]) => void;
  isEnabled?: boolean;
}): void => {
  const { eventEmitter } = useCompanyScopeContext();
  // Stable identity, so the subscription is not torn down and rebuilt every render while the handler
  // still sees current values - and so the same reference reaches `off` that reached `on`.
  const handlerStable = useEvent(handler);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    eventEmitter?.on(event, handlerStable);

    return () => eventEmitter?.off(event, handlerStable);
  }, [event, eventEmitter, handlerStable, isEnabled]);
};
